import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAnalytics } from '../../../../contexts';
import { ModalName } from '../../../../enums';
import { useSendEmail } from '../../../../features/app';
import { useCloseModal } from '../../../../features/modals';
import { areValuesValid } from '../../../../utils';
import { Button, FormFields, Input, Modal } from '../../../common';
import { IContactSupportModalBodyProps } from './types';

export const ContactSupportModal = () => {
  const { t } = useTranslation();
  const [body, setBody] = useState<IContactSupportModalBodyProps>({
    name: '',
    email: '',
    message: '',
  });
  const closeModal = useCloseModal(ModalName.CONTACT_SUPPORT);
  const sendEmail = useSendEmail();
  const analytics = useAnalytics();

  const isValid = useMemo(
    () => areValuesValid([body.name, body.message, { type: 'email', value: body.email }]),
    [body],
  );

  const setName = (name: string) => setBody({ ...body, name });
  const setEmail = (email: string) => setBody({ ...body, email });
  const setMessage = (message: string) => setBody({ ...body, message });

  const send = useCallback(() => {
    analytics.track('Contacting support');

    const bodyString = `
      Name: ${body.name} <br>
      Email: ${body.email} <br>
      Message: ${body.message}
    `;
    const params = {
      subject: 'Support Requested',
      body: bodyString,
    };
    sendEmail(params);
    closeModal();
  }, [sendEmail, closeModal, body]);

  const footer: JSX.Element = useMemo(() => {
    return (
      <div className="flex">
        <Button text={t('modal.support.button.send')} disabled={!isValid} onClick={send} />
        <Button text={t('modal.support.button.cancel')} type="tertiary" onClick={closeModal} className="ml-2" />
      </div>
    );
  }, [closeModal, send, isValid]);

  return (
    <Modal name={ModalName.CONTACT_SUPPORT} header={t('modal.support.title')} footer={footer}>
      <p className="typography-paragraph-3 text-text-secondary mb-6">{t('modal.support.description')}</p>

      <FormFields>
        <Input
          value={body.name}
          label={t('modal.support.field.name.label')}
          onChange={setName}
          placeholder={t('modal.support.field.name.placeholder')}
        />

        <Input
          value={body.email}
          label={t('modal.support.field.email.label')}
          onChange={setEmail}
          placeholder={t('modal.support.field.email.placeholder')}
          type="email"
        />

        <Input
          value={body.message}
          label={t('modal.support.field.message.label')}
          onChange={setMessage}
          placeholder={t('modal.support.field.message.placeholder')}
          type="textarea"
        />
      </FormFields>
    </Modal>
  );
};
